import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LoadingButton } from '@mui/lab';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { client, useAuth, useI18n } from 'iomart-hooks';
import { ROLES } from 'src/constants/roles';
import Iconify from 'src/components/iconify/Iconify';

/** Событие: скроллер заказов Ozon подписан в getTableView и перезагружает список */
export const OZON_POSTINGS_RELOAD_EVENT = 'iomart:ozon-postings-reload';

type AccountOption = { id: number; name: string; hasApiKey: boolean };

const defaultRange = () => ({
  from: dayjs().startOf('month'),
  to: dayjs().endOf('month'),
});

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm:ss') : String(value);
};

export function OzonPostingsLastSyncMeta() {
  const { role, permissions = [] } = (useAuth(false) as any) ?? {};
  const isAdmin = String(role ?? '').toUpperCase() === ROLES.ADMIN;
  const canSeeAccounts = isAdmin || permissions.includes('ozonSellerAccounts');

  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  const loadLastSync = useCallback(async () => {
    if (!canSeeAccounts) return;
    try {
      const res = await (client as any).query({
        listOzonSellerAccounts: {
          items: { id: true, lastSyncAt: true },
          __args: {
            category: 'all',
            paging: { page: 0, size: 500 },
            sorting: [{ by: 'desc', order: 'updatedAt' }],
            filters: {},
          },
        },
      });
      const rows = (res?.listOzonSellerAccounts?.items ?? []) as Array<{ lastSyncAt?: string | null }>;
      const latest = rows
        .map((x) => (x?.lastSyncAt ? String(x.lastSyncAt) : null))
        .filter(Boolean)
        .sort((a, b) => (String(a) > String(b) ? -1 : 1))[0] as string | undefined;
      setLastSyncAt(latest ?? null);
    } catch {
      setLastSyncAt(null);
    }
  }, [canSeeAccounts]);

  useEffect(() => {
    loadLastSync();
  }, [loadLastSync]);

  if (!canSeeAccounts) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Iconify icon="mdi:sync" width={16} color="text.secondary" />
      <Typography variant="caption" color="text.secondary">
        {formatDateTime(lastSyncAt)}
      </Typography>
    </Stack>
  );
}

export function OzonPostingsSyncToolbar() {
  const { role, userInfo } = useAuth(false) as any;
  const { t } = useI18n(false);
  const { enqueueSnackbar } = useSnackbar();
  /** notistack / i18n часто отдают новые ссылки на колбэки → без ref useEffect с [loadAccounts] уходит в цикл. */
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;
  const tRef = useRef(t);
  tRef.current = t;

  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [account, setAccount] = useState<AccountOption | null>(null);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(() => defaultRange().from);
  const [dateTo, setDateTo] = useState<Dayjs | null>(() => defaultRange().to);
  const [syncing, setSyncing] = useState(false);
  const [syncGetData, setSyncGetData] = useState(true);
  const [includeFboGet, setIncludeFboGet] = useState(true);
  const [includeFbsGet, setIncludeFbsGet] = useState(true);
  const [refreshCostPrices, setRefreshCostPrices] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState<number>(5);
  const [lookbackDays, setLookbackDays] = useState<number>(1);
  const [autoTasks, setAutoTasks] = useState<any[]>([]);
  const [creatingAutoTask, setCreatingAutoTask] = useState(false);
  const [taskActionLoadingId, setTaskActionLoadingId] = useState<number | null>(null);
  const [historyAccount, setHistoryAccount] = useState<AccountOption | null>(null);
  const [historyRuns, setHistoryRuns] = useState<any[]>([]);
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const isAdmin = String(role ?? userInfo?.role ?? '').toUpperCase() === 'ADMIN';

  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const res = await client.query({
        listOzonSellerAccounts: {
          items: { id: true, name: true, hasApiKey: true },
          __args: {
            category: 'all',
            paging: { page: 0, size: 500 },
            sorting: [{ by: 'asc', order: 'name' }],
            filters: {},
          },
        },
      });
      const items = (res.listOzonSellerAccounts?.items ?? []) as AccountOption[];
      setAccounts(items);
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? tRef.current('Failed to load accounts'), { variant: 'error' });
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const r = defaultRange();
    setDateFrom(r.from);
    setDateTo(r.to);
    setAccount(null);
    setSyncGetData(true);
    setIncludeFboGet(true);
    setIncludeFbsGet(true);
    setRefreshCostPrices(false);
    setIntervalMinutes(5);
    setLookbackDays(1);
    loadAccounts();
  }, [open, loadAccounts]);

  useEffect(() => {
    if (open && !account && accounts.length > 0) {
      setAccount(accounts[0]);
    }
  }, [open, account, accounts]);

  useEffect(() => {
    if (historyOpen) {
      loadAccounts();
    }
  }, [historyOpen, loadAccounts]);

  const loadHistoryRuns = useCallback(async (accountId: number) => {
    setHistoryLoading(true);
    try {
      const res = await (client as any).query({
        ozonPostingGetSyncRuns: {
          __args: { data: { accountId, page: 0, size: 100 } },
          total: true,
          items: {
            id: true,
            status: true,
            periodFrom: true,
            periodTo: true,
            startedAt: true,
            finishedAt: true,
            totalPlanned: true,
            totalSuccess: true,
            totalFailed: true,
            errorSummary: true,
            createdAt: true,
          },
        },
      });
      setHistoryRuns(res?.ozonPostingGetSyncRuns?.items ?? []);
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? tRef.current('Failed to load sync history'), { variant: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadAutoTasks = useCallback(async (accountId?: number) => {
    try {
      const res = await (client as any).query({
        ozonPostingAutoSyncTasks: {
          __args: { data: { accountId } },
          id: true,
          name: true,
          status: true,
          intervalMinutes: true,
          lookbackDays: true,
          syncGetData: true,
          includeFboGet: true,
          includeFbsGet: true,
          refreshCostPrices: true,
          lastRunAt: true,
          nextRunAt: true,
          lastError: true,
        },
      });
      setAutoTasks(res?.ozonPostingAutoSyncTasks ?? []);
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? tRef.current('Failed to load auto sync tasks'), { variant: 'error' });
    }
  }, []);

  useEffect(() => {
    if (historyOpen && !historyAccount && accounts.length > 0) {
      const first = accounts[0];
      setHistoryAccount(first);
      if (first?.id) {
        setSelectedRunId(null);
        setHistoryItems([]);
        loadHistoryRuns(first.id);
        loadAutoTasks(first.id);
      }
    }
  }, [historyOpen, historyAccount, accounts, loadHistoryRuns, loadAutoTasks]);

  const loadHistoryItems = useCallback(async (runId: number) => {
    try {
      const res = await (client as any).query({
        ozonPostingGetSyncRunItems: {
          __args: { runId },
          id: true,
          postingNumber: true,
          scheme: true,
          status: true,
          attempt: true,
          error: true,
          startedAt: true,
          finishedAt: true,
        },
      });
      setHistoryItems(res?.ozonPostingGetSyncRunItems ?? []);
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? tRef.current('Failed to load run details'), { variant: 'error' });
    }
  }, []);

  const handleSync = async () => {
    if (!account?.id || !dateFrom || !dateTo) {
      enqueueSnackbar(t('Select account and date range'), { variant: 'warning' });
      return;
    }
    if (!account.hasApiKey) {
      enqueueSnackbar(t('This account has no API key'), { variant: 'warning' });
      return;
    }
    if (dateFrom.isAfter(dateTo)) {
      enqueueSnackbar(t('Start date must be before end date'), { variant: 'warning' });
      return;
    }

    setSyncing(true);
    try {
      const result = await (client as any).mutation({
        syncOzonPostings: {
          count: true,
          __args: {
            data: {
              accountId: account.id,
              dateFrom: dateFrom.startOf('day').toDate(),
              dateTo: dateTo.endOf('day').toDate(),
              syncGetData,
              getDateFrom: dateFrom.startOf('day').toDate(),
              getDateTo: dateTo.endOf('day').toDate(),
              includeFboGet,
              includeFbsGet,
              refreshCostPrices,
            },
          },
        },
      });
      const count = result.syncOzonPostings?.count ?? 0;
      enqueueSnackbar(t('Synced {{count}} postings from Ozon', { count }), { variant: 'success' });
      setOpen(false);
      window.dispatchEvent(new Event(OZON_POSTINGS_RELOAD_EVENT));
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? t('Sync failed'), { variant: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateAutoTask = async () => {
    if (!historyAccount?.id) {
      enqueueSnackbar(t('Select account'), { variant: 'warning' });
      return;
    }
    setCreatingAutoTask(true);
    try {
      await (client as any).mutation({
        createOzonPostingAutoSyncTask: {
          id: true,
          __args: {
            data: {
              accountId: historyAccount.id,
              intervalMinutes: Math.max(1, Number(intervalMinutes || 1)),
              lookbackDays: Math.max(1, Number(lookbackDays || 1)),
              syncGetData,
              includeFboGet,
              includeFbsGet,
              refreshCostPrices,
            },
          },
        },
      });
      enqueueSnackbar(t('Auto sync task created'), { variant: 'success' });
      loadAutoTasks(historyAccount.id);
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? t('Failed to create auto sync task'), { variant: 'error' });
    } finally {
      setCreatingAutoTask(false);
    }
  };

  const handleStopAutoTask = async (id: number) => {
    setTaskActionLoadingId(id);
    try {
      await (client as any).mutation({
        stopOzonPostingAutoSyncTask: {
          __args: { id },
        },
      });
      enqueueSnackbar(t('Task stopped'), { variant: 'success' });
      if (historyAccount?.id) loadAutoTasks(historyAccount.id);
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? t('Failed to stop task'), { variant: 'error' });
    } finally {
      setTaskActionLoadingId(null);
    }
  };

  const handleDeleteAutoTask = async (id: number) => {
    setTaskActionLoadingId(id);
    try {
      await (client as any).mutation({
        deleteOzonPostingAutoSyncTask: {
          __args: { id },
        },
      });
      enqueueSnackbar(t('Task deleted'), { variant: 'success' });
      if (historyAccount?.id) loadAutoTasks(historyAccount.id);
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? t('Failed to delete task'), { variant: 'error' });
    } finally {
      setTaskActionLoadingId(null);
    }
  };

  return (
    <>
      {!isAdmin ? null : (
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="contained" color="primary" onClick={() => setOpen(true)} size="medium">
          {t('Sync now')}
        </Button>
        <IconButton size="small" onClick={() => setHistoryOpen(true)}>
          <Iconify icon="mdi:history" width={18} />
        </IconButton>
      </Stack>
      )}

      <Drawer anchor="right" open={open} onClose={() => !syncing && setOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 520 }, p: 2 }}>
          <Stack spacing={2.5}>
            <Typography variant="h6">{t('Sync Ozon postings')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Pulls FBS/FBO postings from Ozon Seller API into the database for the selected period.')}
            </Typography>

            <Autocomplete
              loading={loadingAccounts}
              options={accounts}
              value={account}
              onChange={(_, v) => setAccount(v)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label={t('Ozon account')} size="small" required />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2">{option.name}</Typography>
                    {!option.hasApiKey && (
                      <Typography variant="caption" color="warning.main">
                        {t('No API key')}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DatePicker
                label={t('From')}
                value={dateFrom}
                onChange={(v) => setDateFrom(v)}
                maxDate={dateTo ?? undefined}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label={t('To')}
                value={dateTo}
                onChange={(v) => setDateTo(v)}
                minDate={dateFrom ?? undefined}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox checked={refreshCostPrices} onChange={(_, v) => setRefreshCostPrices(v)} />
              }
              label={t('Update cost prices from catalog (fixed snapshot per line)')}
            />
            <FormControlLabel
              control={<Checkbox checked={syncGetData} onChange={(_, v) => setSyncGetData(v)} />}
              label={t('Queue get-data sync (FBO/FBS get)')}
            />
            {syncGetData && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControlLabel
                  control={<Checkbox checked={includeFboGet} onChange={(_, v) => setIncludeFboGet(v)} />}
                  label="FBO get"
                />
                <FormControlLabel
                  control={<Checkbox checked={includeFbsGet} onChange={(_, v) => setIncludeFbsGet(v)} />}
                  label="FBS get"
                />
              </Stack>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button onClick={() => setOpen(false)} disabled={syncing}>
                {t('Cancel')}
              </Button>
              <LoadingButton
                variant="contained"
                loading={syncing}
                disabled={!account?.id || !account?.hasApiKey || !dateFrom || !dateTo}
                onClick={handleSync}
              >
                {t('Sync')}
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 900 }, p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">{t('Get sync history')}</Typography>
            <Autocomplete
              loading={loadingAccounts}
              options={accounts}
              value={historyAccount}
              onChange={(_, v) => {
                setHistoryAccount(v);
                setSelectedRunId(null);
                setHistoryItems([]);
                if (v?.id) {
                  loadHistoryRuns(v.id);
                  loadAutoTasks(v.id);
                } else {
                  setAutoTasks([]);
                }
              }}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label={t('Ozon account')} size="small" />}
            />

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">{t('Auto sync')}</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    type="number"
                    label={t('Periodicity (minutes)')}
                    value={intervalMinutes}
                    onChange={(e) => setIntervalMinutes(Math.max(1, Number(e.target.value || 1)))}
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label={t('Load last days')}
                    value={lookbackDays}
                    onChange={(e) => setLookbackDays(Math.max(1, Number(e.target.value || 1)))}
                    sx={{ minWidth: 160 }}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControlLabel
                    control={<Checkbox checked={syncGetData} onChange={(_, v) => setSyncGetData(v)} />}
                    label={t('Queue get-data sync')}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={refreshCostPrices} onChange={(_, v) => setRefreshCostPrices(v)} />}
                    label={t('Refresh costs')}
                  />
                </Stack>
                <Stack direction="row" justifyContent="flex-end">
                  <LoadingButton
                    size="small"
                    variant="contained"
                    loading={creatingAutoTask}
                    disabled={!historyAccount?.id}
                    onClick={handleCreateAutoTask}
                  >
                    {t('Create auto task')}
                  </LoadingButton>
                </Stack>
                <Divider />
                <Stack spacing={1}>
                  {autoTasks.map((task) => (
                    <Box
                      key={task.id}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2">
                          #{task.id} • {task.status} • {task.intervalMinutes}m / {task.lookbackDays}d
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('Last run')}: {formatDateTime(task.lastRunAt)} • {t('Next')}: {formatDateTime(task.nextRunAt)}
                        </Typography>
                        {task.lastError ? (
                          <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                            {task.lastError}
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <LoadingButton
                          size="small"
                          color="warning"
                          loading={taskActionLoadingId === task.id}
                          disabled={task.status !== 'active'}
                          onClick={() => handleStopAutoTask(task.id)}
                        >
                          {t('Stop')}
                        </LoadingButton>
                        <LoadingButton
                          size="small"
                          color="error"
                          loading={taskActionLoadingId === task.id}
                          onClick={() => handleDeleteAutoTask(task.id)}
                        >
                          {t('Delete')}
                        </LoadingButton>
                      </Stack>
                    </Box>
                  ))}
                  {autoTasks.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {t('No auto sync tasks')}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Paper>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Finished</TableCell>
                    <TableCell align="right">Planned</TableCell>
                    <TableCell align="right">Success</TableCell>
                    <TableCell align="right">Failed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRuns.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      selected={selectedRunId === r.id}
                      onClick={() => {
                        setSelectedRunId(r.id);
                        loadHistoryItems(r.id);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell>{formatDateTime(r.periodFrom)} - {formatDateTime(r.periodTo)}</TableCell>
                      <TableCell>{formatDateTime(r.startedAt)}</TableCell>
                      <TableCell>{formatDateTime(r.finishedAt)}</TableCell>
                      <TableCell align="right">{r.totalPlanned ?? 0}</TableCell>
                      <TableCell align="right">{r.totalSuccess ?? 0}</TableCell>
                      <TableCell align="right">{r.totalFailed ?? 0}</TableCell>
                    </TableRow>
                  ))}
                  {!historyLoading && historyRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary">
                          No sync runs
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {selectedRunId != null && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Posting</TableCell>
                      <TableCell>Scheme</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Attempt</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyItems.map((x) => (
                      <TableRow key={x.id}>
                        <TableCell>{x.postingNumber}</TableCell>
                        <TableCell>{x.scheme}</TableCell>
                        <TableCell>{x.status}</TableCell>
                        <TableCell align="right">{x.attempt ?? 0}</TableCell>
                        <TableCell>{x.error ?? ''}</TableCell>
                      </TableRow>
                    ))}
                    {historyItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            No run details
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Stack direction="row" justifyContent="flex-end">
              <Button onClick={() => setHistoryOpen(false)}>{t('Close')}</Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
